import { RouterProvider } from 'react-router-dom'
import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAppUpdateCheck } from '@/hooks/use-app-update-check'
import { TestEditor } from './components/test-editor'
import { ChangeTrackingTest } from './components/test-change-tracking'
import { ChatWindowTest } from './components/test-chat-window'
import { ChatWindowThinkingTest } from './components/test-chat-window-thinking'
import { ThinkingModeTest } from './components/test-thinking-mode'
import { PrototypeIndex } from './components/test-prototype-index'
import { TabRedesignTest } from './components/test-tab-redesign'
import { TestVoiceMemos } from './components/test-voice-memos'
import { SecondaryPanelTest } from './components/test-secondary-panel'
import { ProductBoard, ProductBoardDocumentRenderer } from './components/features/product-board'
import { TestCursorWorkspace } from './components/test-cursor-workspace'
import { FolderDashboardTest } from './components/test-folder-dashboard'
import { StructuredDocBlocksTest } from './components/test-structured-doc-blocks'
import { TestBasecampTodos } from './components/test-basecamp-todos'
import { TestBasecampProjectsCache } from './components/test-basecamp-projects-cache'
import { TestOrganizationManagement } from './components/test-organization-management'
import { BrowseNavTest } from './components/test-browse-nav'
import { BrowseNavInlineTest } from './components/test-browse-nav-inline'
import { BrowseFolderFocusTest } from './components/test-browse-folder-focus'
import { DocTocRulerTest } from './components/test-doc-toc-ruler'
import { InboxSplitTest } from './components/test-inbox-split'
import { InboxFoldersTest } from './components/test-inbox-folders'
import { MatterFeedTest } from './components/test-matter-feed'
import { FlowEditorTest } from './components/test-flow-editor'
import { FlowsStudioTest } from './components/test-flows-studio'
import { MobileAppTest } from './components/test-mobile-app'
import { AgentsLayoutTest } from './components/test-agents-layout'
import { GoogleDriveExtensionTest } from './components/test-google-drive-extension'
import { AsanaExtensionTest } from './components/test-asana-extension'
import { BasecampExtensionTest } from './components/test-basecamp-extension'
import TestNextcloudExtension from './components/test-nextcloud-extension'
import { BasecampProjectFolderMappingsTest } from './components/test-basecamp-project-folder-mappings'
import { KnowledgeHubChangelogTest } from './components/test-knowledge-hub-changelog'
import { DocumentVersionHistoryTest } from './components/test-document-version-history'
import { TestFeedbackModal } from './components/test-feedback-modal'
import { StreamingAgentTest } from './components/test-streaming-agent'
import { TestAiRenameSuggest } from './components/test-ai-rename-suggest'
import { TestWorkspaceActivity } from './components/test-workspace-activity'
import { QuizLmsPrototype } from './components/test-quiz-lms'
import { QuizDomainEscapeWireframe } from './components/test-quiz-domain-escape'
import { MobileComposerDWireframes } from './components/test-mobile-composer-d'
import { ChatQueueEditPrototype } from './components/test-chat-queue-edit'
import { TestViewerFrontmatterBar } from './components/test-viewer-frontmatter-bar'
import { TtsLessonAudioPrototype } from './components/test-tts-lesson-audio'
import { BrowseDeskSidebarPrototype } from './components/test-browse-desk-sidebar'
import { WorkspaceSettingsIaTest } from './components/test-workspace-settings-ia'
import { MaaterUiGalleryTest } from './components/test-maater-ui'
import { DocCommentsPrototype } from './components/test-doc-comments'
import { registerRenderer } from '@/lib/document-renderer-registry'
import { registerDocumentKindIcon } from '@/lib/document-icons'
import { FlowDocumentRenderer } from '@/components/features/flows/FlowDocumentRenderer'
import { QuizDocumentRenderer } from '@/components/integrations/quiz/QuizDocumentRenderer'
import { SessionImageDocumentRenderer } from '@/components/features/session-image-document-renderer'
import { ImageDocumentViewer } from '@/components/features/image-document-viewer'
import { GraduationCap } from 'lucide-react'
import { IntegrationProvider } from './providers/integration-provider'
import { ThemeProvider } from './providers/theme-provider'
import { AuthProvider } from './providers/auth-provider'
import { WorkspaceProvider } from './providers/workspace-provider'
import { Toaster } from './components/ui/toaster'
import { MermaidLightboxHost } from './components/features/mermaid-lightbox-host'
import { TooltipProvider } from './components/ui/tooltip'
import { BasecampOAuthDonePage } from './components/oauth/basecamp-oauth-done-page'
import { GoogleDriveOAuthDonePage } from './components/oauth/google-drive-oauth-done-page'
import { router } from './routes'

registerRenderer('flow', FlowDocumentRenderer)
registerRenderer('quiz', QuizDocumentRenderer)
registerRenderer('product-board', ProductBoardDocumentRenderer)
registerRenderer('session_image', SessionImageDocumentRenderer)
registerRenderer('image', ImageDocumentViewer, {
  saveable: false,
  openInCursor: false,
  download: 'original',
  fileExtensions: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'],
})
registerDocumentKindIcon('quiz', { icon: GraduationCap, colorClass: 'text-teal-600' })

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

function AppUpdateWatcher() {
  useAppUpdateCheck(true)
  return null
}

function App() {
  // Toggle test modes
  const testEditor = window.location.search.includes('test=editor')
  const testChangeTracking = window.location.search.includes('test=change-tracking')
  const testChatWindowLegacy = window.location.search.includes('test=chat-window-legacy')
  const testChatWindow =
    window.location.search.includes('test=chat-window') && !testChatWindowLegacy
  const testThinkingMode = window.location.search.includes('test=thinking-mode')
  const testTabRedesign = window.location.search.includes('test=tab-redesign')
  const testVoiceMemos = window.location.search.includes('test=voice-memos')
  const testSecondaryPanel = window.location.search.includes('test=secondary-panel')
  const testProductManagement = window.location.search.includes('test=product-management')
  const testCursorWorkspace = window.location.search.includes('test=cursor-workspace')
  const testFolderDashboard = window.location.search.includes('test=folder-dashboard')
  const testStructuredDocBlocks = window.location.search.includes('test=structured-doc-blocks')
  const testBasecampTodos = window.location.search.includes('test=basecamp-todos')
  const testBasecampProjectsCache = window.location.search.includes('test=basecamp-projects-cache')
  const testOrganizationManagement = window.location.search.includes('test=organization-management')
  const testBrowseNav = window.location.search.includes('test=browse-nav') && !window.location.search.includes('test=browse-nav-inline')
  const testBrowseNavInline = window.location.search.includes('test=browse-nav-inline')
  const testBrowseFolderFocus = window.location.search.includes('test=browse-folder-focus')
  const testBrowseLevelFocus = window.location.search.includes('test=browse-level-focus')
  const testIndex = window.location.search.includes('test=index')
  const testDocTocRuler = window.location.search.includes('test=doc-toc-ruler')
  const testInboxSplit = window.location.search.includes('test=inbox-split')
  const testInboxFolders = window.location.search.includes('test=inbox-folders')
  const testMatterFeed = window.location.search.includes('test=matter-feed')
  const testFlowEditor = window.location.search.includes('test=flow-editor')
  const testFlowsStudio = window.location.search.includes('test=flows-studio')
  const testMobileApp = window.location.search.includes('test=mobile-app')
  const testAgentsLayout = window.location.search.includes('test=agents-layout')
  const testGoogleDriveExtension = window.location.search.includes('test=google-drive-extension')
  const testAsanaExtension = window.location.search.includes('test=asana-extension')
  const testBasecampExtension = window.location.search.includes('test=basecamp-extension')
  const testNextcloudExtension = window.location.search.includes('test=nextcloud-extension')
  const testBasecampProjectFolderMappings = window.location.search.includes(
    'test=basecamp-project-folder-mappings',
  )
  const testWorkspaceSettingsIa = window.location.search.includes('test=workspace-settings-ia')
  const testKnowledgeHubChangelog = window.location.search.includes('test=knowledge-hub-changelog')
  const testDocumentVersionHistory = window.location.search.includes('test=document-version-history')
  const testFeedbackModal = window.location.search.includes('test=feedback-modal')
  const testStreamingAgent = window.location.search.includes('test=streaming-agent')
  const testAiRenameSuggest = window.location.search.includes('test=ai-rename-suggest')
  const testWorkspaceActivity = window.location.search.includes('test=workspace-activity')
  const testQuizDomainEscape = window.location.search.includes('test=quiz-domain-escape')
  const testMobileComposerD = window.location.search.includes('test=mobile-composer-d')
  const testChatQueueEdit = window.location.search.includes('test=chat-queue-edit')
  const testQuizLms =
    window.location.search.includes('test=quiz') && !testQuizDomainEscape
  const testViewerFrontmatterBar = window.location.search.includes('test=viewer-frontmatter-bar')
  const testTtsLessonAudio = window.location.search.includes('test=tts-lesson-audio')
  const testBrowseDeskSidebar = window.location.search.includes('test=browse-desk-sidebar')
  const testMaaterUi = window.location.search.includes('test=maater-ui')
  const testDocComments = window.location.search.includes('test=doc-comments')

  // Redirect old /list URLs to / (document list is at root)
  useEffect(() => {
    if (window.location.pathname === '/list') {
      window.history.replaceState({}, '', '/')
    }
  }, [])

  if (testEditor) {
    return <TestEditor />
  }

  if (testChangeTracking) {
    return <ChangeTrackingTest />
  }

  if (testChatWindow) {
    return (
      <ThemeProvider>
        <ChatWindowThinkingTest />
      </ThemeProvider>
    )
  }

  if (testChatWindowLegacy) {
    return <ChatWindowTest />
  }

  if (testThinkingMode) {
    return (
      <ThemeProvider>
        <ThinkingModeTest />
      </ThemeProvider>
    )
  }

  if (testTabRedesign) {
    return (
      <ThemeProvider>
        <TabRedesignTest />
      </ThemeProvider>
    )
  }

  if (testVoiceMemos) {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TestVoiceMemos />
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  if (testSecondaryPanel) {
    return (
      <ThemeProvider>
        <SecondaryPanelTest />
      </ThemeProvider>
    )
  }

  if (testProductManagement) {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <WorkspaceProvider>
              <TooltipProvider>
                <ProductBoard />
              </TooltipProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  if (testCursorWorkspace) {
    return (
      <ThemeProvider>
        <TestCursorWorkspace />
      </ThemeProvider>
    )
  }

  if (testFolderDashboard) {
    return (
      <ThemeProvider>
        <FolderDashboardTest />
      </ThemeProvider>
    )
  }

  if (testStructuredDocBlocks) {
    return (
      <ThemeProvider>
        <StructuredDocBlocksTest />
      </ThemeProvider>
    )
  }

  if (testBasecampTodos) {
    return (
      <ThemeProvider>
        <TestBasecampTodos />
      </ThemeProvider>
    )
  }

  if (testBasecampProjectsCache) {
    return (
      <ThemeProvider>
        <TestBasecampProjectsCache />
      </ThemeProvider>
    )
  }

  if (testOrganizationManagement) {
    return (
      <ThemeProvider>
        <TestOrganizationManagement />
      </ThemeProvider>
    )
  }

  if (testBrowseNav) {
    return (
      <ThemeProvider>
        <BrowseNavTest />
      </ThemeProvider>
    )
  }

  if (testBrowseNavInline) {
    return (
      <ThemeProvider>
        <TooltipProvider>
          <BrowseNavInlineTest />
        </TooltipProvider>
      </ThemeProvider>
    )
  }

  if (testBrowseFolderFocus || testBrowseLevelFocus) {
    return (
      <ThemeProvider>
        <BrowseFolderFocusTest initialTreeTab={testBrowseLevelFocus ? 'specs' : 'clients'} />
      </ThemeProvider>
    )
  }

  if (testMaaterUi) {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <WorkspaceProvider>
              <TooltipProvider>
                <MaaterUiGalleryTest />
              </TooltipProvider>
            </WorkspaceProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  if (testDocComments) {
    return (
      <ThemeProvider>
        <DocCommentsPrototype />
      </ThemeProvider>
    )
  }

  if (testIndex) {
    return (
      <ThemeProvider>
        <PrototypeIndex />
      </ThemeProvider>
    )
  }

  if (testDocTocRuler) {
    return (
      <ThemeProvider>
        <DocTocRulerTest />
      </ThemeProvider>
    )
  }

  if (testInboxFolders) {
    return (
      <ThemeProvider>
        <TooltipProvider>
          <InboxFoldersTest />
        </TooltipProvider>
      </ThemeProvider>
    )
  }

  if (testMatterFeed) {
    return (
      <ThemeProvider>
        <MatterFeedTest />
      </ThemeProvider>
    )
  }

  if (testInboxSplit) {
    return (
      <ThemeProvider>
        <InboxSplitTest />
      </ThemeProvider>
    )
  }

  if (testFlowsStudio) {
    return (
      <ThemeProvider>
        <FlowsStudioTest />
      </ThemeProvider>
    )
  }

  if (testFlowEditor) {
    return (
      <ThemeProvider>
        <FlowEditorTest />
      </ThemeProvider>
    )
  }

  if (testAgentsLayout) {
    return (
      <ThemeProvider>
        <TooltipProvider>
          <AgentsLayoutTest />
        </TooltipProvider>
      </ThemeProvider>
    )
  }

  if (testMobileApp) {
    return <MobileAppTest />
  }

  if (testGoogleDriveExtension) {
    return (
      <ThemeProvider>
        <TooltipProvider>
          <GoogleDriveExtensionTest />
        </TooltipProvider>
      </ThemeProvider>
    )
  }

  if (testAsanaExtension) {
    return (
      <ThemeProvider>
        <AsanaExtensionTest />
      </ThemeProvider>
    )
  }

  if (testBasecampProjectFolderMappings) {
    return (
      <ThemeProvider>
        <BasecampProjectFolderMappingsTest />
      </ThemeProvider>
    )
  }

  if (testBasecampExtension) {
    return (
      <ThemeProvider>
        <AuthProvider>
          <BasecampExtensionTest />
        </AuthProvider>
      </ThemeProvider>
    )
  }

  if (testNextcloudExtension) {
    return (
      <ThemeProvider>
        <TestNextcloudExtension />
      </ThemeProvider>
    )
  }

  if (testWorkspaceSettingsIa) {
    return (
      <ThemeProvider>
        <WorkspaceSettingsIaTest />
      </ThemeProvider>
    )
  }

  if (testKnowledgeHubChangelog) {
    return (
      <ThemeProvider>
        <KnowledgeHubChangelogTest />
      </ThemeProvider>
    )
  }

  if (testDocumentVersionHistory) {
    return (
      <ThemeProvider>
        <DocumentVersionHistoryTest />
      </ThemeProvider>
    )
  }

  if (testQuizDomainEscape) {
    return (
      <ThemeProvider>
        <QuizDomainEscapeWireframe />
      </ThemeProvider>
    )
  }

  if (testMobileComposerD) {
    return (
      <ThemeProvider>
        <MobileComposerDWireframes />
      </ThemeProvider>
    )
  }

  if (testChatQueueEdit) {
    return (
      <ThemeProvider>
        <ChatQueueEditPrototype />
      </ThemeProvider>
    )
  }

  if (testQuizLms) {
    return (
      <ThemeProvider>
        <QuizLmsPrototype />
      </ThemeProvider>
    )
  }

  if (testViewerFrontmatterBar) {
    return (
      <ThemeProvider>
        <TestViewerFrontmatterBar />
      </ThemeProvider>
    )
  }

  if (testTtsLessonAudio) {
    return (
      <ThemeProvider>
        <TtsLessonAudioPrototype />
      </ThemeProvider>
    )
  }

  if (testBrowseDeskSidebar) {
    return (
      <ThemeProvider>
        <BrowseDeskSidebarPrototype />
      </ThemeProvider>
    )
  }

  if (testFeedbackModal) {
    return (
      <ThemeProvider>
        <TestFeedbackModal />
      </ThemeProvider>
    )
  }

  if (testStreamingAgent) {
    return (
      <ThemeProvider>
        <StreamingAgentTest />
      </ThemeProvider>
    )
  }

  if (testAiRenameSuggest) {
    return (
      <ThemeProvider>
        <TestAiRenameSuggest />
      </ThemeProvider>
    )
  }

  if (testWorkspaceActivity) {
    return (
      <ThemeProvider>
        <TooltipProvider>
          <TestWorkspaceActivity />
        </TooltipProvider>
      </ThemeProvider>
    )
  }

  // Basecamp OAuth completion:
  // never runs ProtectedLayout / AppLayout for this path (avoids v7 match + errorElement bugs).
  const path = typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') : ''
  if (path === '/oauth/basecamp-done') {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <BasecampOAuthDonePage />
            <Toaster />
            <MermaidLightboxHost />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  if (path === '/oauth/google-drive-done') {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <GoogleDriveOAuthDonePage />
            <Toaster />
            <MermaidLightboxHost />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <IntegrationProvider>
            <WorkspaceProvider>
              <TooltipProvider>
                <RouterProvider router={router} />
                <AppUpdateWatcher />
                <Toaster />
                <MermaidLightboxHost />
              </TooltipProvider>
            </WorkspaceProvider>
          </IntegrationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
